"use client";

import {
	deriveAddToCartState,
	isCartDisabledByState
} from "@/composites/cart/AddToCartButton";
import { type CatalogProduct, type OnlineStatus } from "@/domain/catalog";
import {
	availabilityLabel,
	isPriceVisible
} from "@/domain/catalog/online-status";
import { useEffect, type ReactNode } from "react";
import { selectHighlightTags } from "@/composites/product/ProductHighlightTag";
import { selectProductMessages } from "@/composites/product/ProductMessages";
import {
	ProductSummaryView,
	type ProductSummaryViewProps
} from "./ProductSummaryView";

export interface ProductSummaryProps {
	product: CatalogProduct;
	onError?: (message: string) => void;
	onSuccess?: () => void;
	addToList?: ReactNode;
	className?: string;
	quantity: number;
	onQuantityChange: (quantity: number) => void;
	addToCart: {
		onClick: () => void | Promise<void>;
		loading: boolean;
		error: string | null;
	};
	paymentMessage?: ReactNode;
	lowStockLabel?: string;
	purchaseLimitMessage?: string;
}

/**
 * Wired ProductSummary — derives every input to `ProductSummaryView` from
 * a `CatalogProduct`, quantity state, and the AddToCart handler props.
 * Selects the highlight tags and message rows the View renders, resolving
 * each date window at render time.
 */
export function ProductSummary({
	product,
	onError,
	addToList,
	className,
	quantity,
	onQuantityChange,
	addToCart,
	paymentMessage,
	lowStockLabel,
	purchaseLimitMessage
}: ProductSummaryProps) {
	useEffect(() => {
		if (addToCart.error && onError) onError(addToCart.error);
	}, [addToCart.error, onError]);

	const showPrice = isPriceVisible(product.onlineStatus);
	const display = product.salePrice;
	const list = product.listPrice;
	const isOnSale =
		typeof display === "number" &&
		typeof list === "number" &&
		display < list;

	const ratingProp: ProductSummaryViewProps["rating"] =
		typeof product.starsRating === "number" &&
		typeof product.numRatings === "number" &&
		product.numRatings > 0
			? {
					value: product.starsRating,
					count: product.numRatings,
					reviewsHref: "#reviews"
				}
			: undefined;

	const availabilityTag = computeAvailabilityTag(product.onlineStatus);

	const addToCartState = deriveAddToCartState(product, {
		loading: addToCart.loading,
		error: addToCart.error
	});

	const cartDisabled = isCartDisabledByState(addToCartState.kind);
	const canAddToLists =
		product.onlineStatus !== "discontinued" &&
		product.onlineStatus !== "inactive";

	const maxQuantity = product.purchaseLimit.quantity;
	const atLimit = quantity >= maxQuantity;

	const highlightTags = selectHighlightTags(product.productTags);
	const messages = selectProductMessages(product.productMessages);

	return (
		<ProductSummaryView
			title={product.title}
			sku={product.sku}
			rating={ratingProp}
			pricing={
				showPrice
					? {
							display,
							strikeThrough: isOnSale ? list : undefined,
							halfPriceCombo: product.halfPriceComboEligible
						}
					: undefined
			}
			highlightTags={highlightTags}
			messages={messages}
			availabilityTag={availabilityTag}
			lowStockLabel={lowStockLabel}
			shortDescription={replacementPartShortDescription(product)}
			quantity={
				cartDisabled
					? undefined
					: {
							value: quantity,
							onChange: onQuantityChange,
							min: 1,
							max: maxQuantity,
							description: atLimit
								? undefined
								: purchaseLimitMessage,
							errorMessage: atLimit
								? purchaseLimitMessage
								: undefined
						}
			}
			purchaseDisclaimers={
				cartDisabled ? undefined : product.purchaseDisclaimers
			}
			addToCart={{
				state: addToCartState,
				onClick: addToCart.onClick
			}}
			addToList={canAddToLists ? addToList : undefined}
			paymentMessage={showPrice ? paymentMessage : undefined}
			className={className}
		/>
	);
}

function replacementPartShortDescription(
	product: Pick<
		CatalogProduct,
		"isReplacement" | "categories" | "shortDescription"
	>
): string | undefined {
	if (!isReplacementPart(product)) return undefined;
	return product.shortDescription?.trim() || undefined;
}

function isReplacementPart(
	product: Pick<CatalogProduct, "isReplacement" | "categories">
): boolean {
	if (product.isReplacement) return true;
	return product.categories.some(
		(category) =>
			category.type === "superCategory" &&
			category.code === "replacement-parts"
	);
}

function computeAvailabilityTag(
	status: OnlineStatus
): { label: string; color: "red" | "grey" } | undefined {
	const label = availabilityLabel(status);
	if (!label) return undefined;

	return { label, color: status === "out-of-stock" ? "red" : "grey" };
}
